import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { CheckCircleIcon } from "lucide-react";
import { Cancel } from "mdi-material-ui";
import {
	ImportOption,
	isValidPageData,
	PageData,
} from "../types/pageBuilderTypes";

interface Props {
	currentPageData: PageData;
	onUpdate: (updatedData: PageData) => void;
}

const PageBuilderImporter: React.FC<Props> = ({
	currentPageData,
	onUpdate,
}) => {
	// State for the dialog
	const [open, setOpen] = useState<boolean>(false);
	const [importedData, setImportedData] = useState<PageData | null>(null);
	const [importOption, setImportOption] = useState<ImportOption>(
		ImportOption.REPLACE
	);
	const [pasteError, setPasteError] = useState<string | null>(null);
	const [isClipboardAvailable, setIsClipboardAvailable] =
		useState<boolean>(true);
	const [manualInput, setManualInput] = useState<string>("");

	// State for notification
	const [notification, setNotification] = useState<{
		open: boolean;
		message: string;
		severity: "success" | "error" | "info" | "warning";
	}>({
		open: false,
		message: "",
		severity: "info",
	});

	// Ref for paste area
	const pasteAreaRef = useRef<HTMLDivElement | null>(null);
	const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

	// Check if clipboard API is available
	useEffect(() => {
		// if (
		// 	navigator &&
		// 	navigator.clipboard &&
		// 	typeof navigator.clipboard.readText === "function"
		// ) {
		// 	const data = navigator.clipboard.readText();
		// 	const isValid = isValidPageData(data);
		// 	if (isValid) {
		// 		setIsClipboardAvailable(true);
		// 	}
		// }
		// setIsClipboardAvailable(
		// 	navigator &&
		// 		navigator.clipboard &&
		// 		typeof navigator.clipboard.readText === "function"
		// );
	}, []);

	// Function to validate if the pasted content is a valid page builder JSON
	const validatePageBuilderJSON = (jsonString: string): PageData | null => {
		try {
			const data = JSON.parse(jsonString);
			console.log("Parsed data:", data); // Debugging line
			// Use the type guard from our types file
			if (isValidPageData(data)) {
				return data;
			}

			return null;
		} catch (error) {
			console.error("Error parsing JSON:", error); // Debugging line
			return null;
		}
	};

	// Handle paste event
	const handlePaste = async () => {
		try {
			if (isClipboardAvailable) {
				const clipboardText = await navigator.clipboard.readText();
				processJsonInput(clipboardText);
			} else {
				// Focus the textarea for manual input if clipboard API isn't available
				if (textAreaRef.current) {
					textAreaRef.current.focus();
				}
			}
		} catch (error) {
			setPasteError(
				"Error accessing clipboard. Please try manual input."
			);
			setNotification({
				open: true,
				message:
					"Error accessing clipboard. Please use the manual input field below.",
				severity: "warning",
			});
		}
	};

	// Process manually entered or pasted JSON
	const processJsonInput = (jsonText: string) => {
		const parsedData = validatePageBuilderJSON(jsonText);

		if (parsedData) {
			setImportedData(parsedData);
			setPasteError(null);
			setManualInput("");
			setOpen(true);
		} else {
			setPasteError("Invalid page builder JSON format");
			setNotification({
				open: true,
				message: "Invalid page builder JSON format",
				severity: "error",
			});
		}
	};

	// Handle manual JSON input
	const handleManualJsonSubmit = () => {
		if (manualInput.trim()) {
			processJsonInput(manualInput);
		} else {
			setPasteError("Please enter JSON data");
		}
	};

	// Handle dialog close
	const handleClose = () => {
		setOpen(false);
		setImportedData(null);
		setImportOption(ImportOption.REPLACE);
	};

	// Handle import confirmation
	const handleImport = () => {
		if (!importedData) return;

		let updatedPageData: PageData;

		if (importOption === ImportOption.REPLACE) {
			// Replace only widgets, products, categories, and settings
			updatedPageData = {
				...currentPageData,
				widgets: importedData.widgets,
				products: importedData.products,
				categories: importedData.categories,
				brands: importedData.brands,
				// Add other fields that should be replaced as needed
			};
		} else {
			// Add widgets to the bottom
			updatedPageData = {
				...currentPageData,
				widgets: [...currentPageData.widgets, ...importedData.widgets],
			};
		}

		// Call the onUpdate callback with the updated data
		onUpdate(updatedPageData);

		// Show success notification with details about what was imported
		const widgetCount = importedData.widgets.length;
		const productCount = importedData.products.length;
		const categoryCount = importedData.categories.length;

		let detailMessage = "";
		if (importOption === ImportOption.REPLACE) {
			detailMessage = `Replaced ${widgetCount} widgets, ${productCount} products, and ${categoryCount} categories`;
		} else {
			detailMessage = `Added ${widgetCount} widgets to the bottom of the page`;
		}

		setNotification({
			open: true,
			message: `${
				importOption === ImportOption.REPLACE
					? "Page content replaced"
					: "Widgets added"
			} successfully. ${detailMessage}`,
			severity: "success",
		});

		handleClose();
	};

	// Handle notification close
	const handleNotificationClose = () => {
		setNotification({
			...notification,
			open: false,
		});
	};

	// Function to copy JSON to clipboard
	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(
				JSON.stringify(currentPageData, null, 2)
			);
			setNotification({
				open: true,
				message: "Copied to clipboard successfully!",
				severity: "success",
			});
		} catch (err) {
			setNotification({
				open: true,
				message: "Failed to copy to clipboard.",
				severity: "error",
			});
		}
	};

	// Function to handle keyboard shortcuts
	const handleKeyDown = (e: React.KeyboardEvent) => {
		// Check for Ctrl+V or Cmd+V
		if ((e.ctrlKey || e.metaKey) && e.key === "v") {
			handlePaste();
		}
	};

	return (
		<div className="page-builder-importer">
			<Card className="mb-6">
				<CardContent className="pt-6">
					<h2 className="text-xl font-bold mb-4">
						Import Page Builder Data
					</h2>

					{isClipboardAvailable ? (
						<div
							className="border-2 border-dashed p-8 text-center rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
							onClick={handlePaste}
							onKeyDown={handleKeyDown}
							tabIndex={0}
							ref={pasteAreaRef}>
							<p className="mb-4">
								Click here to paste page builder JSON from
								clipboard
							</p>
							<p className="text-sm text-gray-500">
								Or use keyboard shortcut:{" "}
								{navigator.platform.includes("Mac")
									? "⌘+V"
									: "Ctrl+V"}
							</p>

							{pasteError && (
								<p className="mt-4 text-red-500">
									{pasteError}
								</p>
							)}
						</div>
					) : (
						<div className="border-2 border-dashed p-6 rounded-lg">
							<p className="mb-4 text-center">
								Enter page builder JSON below:
							</p>
							<TextField
								multiline
								rows={6}
								fullWidth
								variant="outlined"
								placeholder="Paste JSON here..."
								value={manualInput}
								onChange={(e) => setManualInput(e.target.value)}
								inputRef={textAreaRef}
								className="mb-4"
							/>
							<Button
								variant="contained"
								color="primary"
								fullWidth
								onClick={handleManualJsonSubmit}>
								Process JSON
							</Button>

							{pasteError && (
								<p className="mt-4 text-red-500 text-center">
									{pasteError}
								</p>
							)}
						</div>
					)}

					<div className="flex justify-between items-center mt-4">
						{isClipboardAvailable && (
							<Button
								variant="outlined"
								color="primary"
								className="mt-4"
								onClick={handlePaste}>
								Paste from Clipboard
							</Button>
						)}

						<Button
							variant="contained"
							color="primary"
							className="mt-4 ml-4"
							onClick={copyToClipboard}>
							Copy Builder Page
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Confirmation Dialog */}
			<Dialog open={open} onClose={handleClose} maxWidth="md">
				<DialogTitle>
					Import Page Builder Data
					{importedData && (
						<Typography
							variant="subtitle2"
							color="textSecondary"
							className="mt-1">
							From: {importedData.name} ({importedData.type})
						</Typography>
					)}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Successfully detected valid page builder data.
					</DialogContentText>

					<Box mt={3} mb={2}>
						<Typography variant="h6">Import Options:</Typography>
					</Box>

					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						<Paper
							elevation={
								importOption === ImportOption.REPLACE ? 3 : 1
							}
							className={`p-4 border ${
								importOption === ImportOption.REPLACE
									? "border-blue-500"
									: "border-gray-200"
							} rounded cursor-pointer transition-all`}
							onClick={() =>
								setImportOption(ImportOption.REPLACE)
							}>
							<Typography
								variant="h6"
								color={
									importOption === ImportOption.REPLACE
										? "primary"
										: "inherit"
								}>
								Replace Content
							</Typography>
							<Typography
								variant="body2"
								color="textSecondary"
								className="mt-1">
								Only widgets, products, categories, and settings
								will be replaced. Page ID, name, and other
								metadata will be preserved.
							</Typography>
						</Paper>

						<Paper
							elevation={
								importOption === ImportOption.ADD ? 3 : 1
							}
							className={`p-4 border ${
								importOption === ImportOption.ADD
									? "border-blue-500"
									: "border-gray-200"
							} rounded cursor-pointer transition-all`}
							onClick={() => setImportOption(ImportOption.ADD)}>
							<Typography
								variant="h6"
								color={
									importOption === ImportOption.ADD
										? "primary"
										: "inherit"
								}>
								Add Widgets to Bottom
							</Typography>
							<Typography
								variant="body2"
								color="textSecondary"
								className="mt-1">
								New widgets will be appended to the existing
								page. No existing content will be modified.
							</Typography>
						</Paper>
					</div>

					{importedData && (
						<Box mt={4}>
							<Typography variant="h6" gutterBottom>
								Summary of imported data:
							</Typography>
							<TableContainer component={Paper} className="mt-2">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Element</TableCell>
											<TableCell align="right">
												Count
											</TableCell>
											<TableCell>
												Will be Imported?
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										<TableRow>
											<TableCell>Widgets</TableCell>
											<TableCell align="right">
												{importedData.widgets.length}
											</TableCell>
											<TableCell>
												<CheckCircleIcon
													color="success"
													fontSize="small"
												/>
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>Products</TableCell>
											<TableCell align="right">
												{importedData.products.length}
											</TableCell>
											<TableCell>
												{importOption ===
												ImportOption.REPLACE ? (
													<CheckCircleIcon
														color="success"
														fontSize="small"
													/>
												) : (
													<Cancel
														color="disabled"
														fontSize="small"
													/>
												)}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>Categories</TableCell>
											<TableCell align="right">
												{importedData.categories.length}
											</TableCell>
											<TableCell>
												{importOption ===
												ImportOption.REPLACE ? (
													<CheckCircleIcon
														color="success"
														fontSize="small"
													/>
												) : (
													<Cancel
														color="disabled"
														fontSize="small"
													/>
												)}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>Brands</TableCell>
											<TableCell align="right">
												{importedData.brands.length}
											</TableCell>
											<TableCell>
												{importOption ===
												ImportOption.REPLACE ? (
													<CheckCircleIcon
														color="success"
														fontSize="small"
													/>
												) : (
													<Cancel
														color="disabled"
														fontSize="small"
													/>
												)}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="inherit">
						Cancel
					</Button>
					<Button
						onClick={handleImport}
						color="primary"
						variant="contained">
						Import
					</Button>
				</DialogActions>
			</Dialog>

			{/* Notification Snackbar */}
			<Snackbar
				open={notification.open}
				autoHideDuration={6000}
				onClose={handleNotificationClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					onClose={handleNotificationClose}
					severity={notification.severity}>
					{notification.message}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default PageBuilderImporter;
